import React from 'react';
import { Button } from '../ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuPortal } from '../ui/dropdown-menu';
import { isToday, isTomorrow, parseISO, format } from 'date-fns';
import { Clock, Circle, CheckCircle2, GripVertical, Paperclip, Trash2, MoreHorizontal, ArrowRightLeft } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableTask = ({ task, onToggle, onDelete, onEdit, attachmentCounts, onOpenAttachments, allLists, currentListId, onMove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isCompleted = task.status === 'completed';
  const priorityColors = {
    urgent: 'border-l-red-500',
    high: 'border-l-orange-500',
    normal: 'border-l-blue-500',
    low: 'border-l-gray-400'
  };

  const attachCount = attachmentCounts?.[task.id] || 0;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 rounded-lg border-l-4 ${priorityColors[task.priority]} bg-card border border-border hover:shadow-sm transition-all group ${
        isCompleted ? 'opacity-60' : ''
      } ${isDragging ? 'shadow-lg z-50' : ''}`}
      data-testid={`task-${task.id}`}
    >
      <div className="flex items-start gap-3">
        <button
          {...attributes}
          {...listeners}
          className="mt-1 text-muted-foreground hover:text-foreground cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        
        <button
          onClick={() => onToggle(task)}
          className="mt-0.5 flex-shrink-0"
          data-testid={`toggle-task-${task.id}`}
        >
          {isCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground hover:text-primary" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
            {task.title}
          </p>
          {task.description && (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
              {task.description}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {task.dueAt && (
              <span className={`text-xs flex items-center gap-1 ${
                isToday(parseISO(task.dueAt)) ? 'text-orange-600' : 'text-muted-foreground'
              }`}>
                <Clock className="h-3 w-3" />
                {isToday(parseISO(task.dueAt)) ? 'Today' : 
                 isTomorrow(parseISO(task.dueAt)) ? 'Tomorrow' :
                 format(parseISO(task.dueAt), 'MMM d')}
              </span>
            )}
            {task.assigneeName && (
              <span className="text-xs text-muted-foreground">
                → {task.assigneeName}
              </span>
            )}
            {attachCount > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                <Paperclip className="h-3 w-3" />
                {attachCount}
              </span>
            )}
          </div>
        </div>
        
        <button
          onClick={() => onOpenAttachments(task)}
          className="mt-1 text-muted-foreground hover:text-primary transition-colors"
          title="Attachments"
          data-testid={`attachments-btn-${task.id}`}
        >
          <Paperclip className="h-4 w-4" />
        </button>

        {allLists && allLists.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`task-menu-${task.id}`}>
                <MoreHorizontal className="h-3 w-3 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  <ArrowRightLeft className="h-3.5 w-3.5 mr-2" /> Move to...
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="max-h-60 overflow-y-auto">
                    {allLists.filter(l => l.id !== currentListId).map(l => (
                      <DropdownMenuItem key={l.id} onClick={() => onMove(task.id, l.id, l.name)} data-testid={`move-task-to-${l.id}`}>
                        <span className="truncate">{l.name}</span>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={() => onDelete(task)}>
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
          onClick={() => onDelete(task)}
          data-testid={`delete-task-${task.id}`}
        >
          <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
        </Button>
      </div>
    </div>
  );
};

export default SortableTask;
